import { Col, DropdownGroup, DropdownMultiselect, Grid, Loader, Row, Table } from "xps-react";
import { Badge, Button, Dropdown, DropdownItem, Search, } from "../../components";
import React, { useEffect, useMemo, useState } from "react";
import NationalSamplesSection, { NationalSampleFilterOption } from "./NationalSamplesSection";
import { clientManagementApiClient } from "../ClientManagementApiClient";
import { toDisplayDateFormat } from "../../utils/dateUtils";
import {
    Column,
    conditionFilterOptions,
    dashboardVariables,
    FilterProfileOptions,
    lastModifiedSortType,
    lastModifiedSortTypeLegalAgreement,
    PARTNER_DASHBOARD_MIN_ROWS,
    PARTNER_DASHBOARD_PAGE_SIZES,
    sortClientMapping,
    sortLAMapping,
    sortLegalAgreements,
    sortProfiles,
    SortType,
} from "./partnerDashboardConstants";
import { Link, useHistory } from "react-router-dom";
import {
    getEmptyNewClientFormInteractions,
    getEmptyProfileRequestForm,
    setIsSaveButtonClicked,
    setNewClientFormInteractions,
    setProfileRequestForm,
} from "../NewClientProfile/NewClientProfileSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
    DropdownItemNames,
    PartnerDashboardProfile,
    PartnerDashboardProfilesSortingInfo,
    ProfileSortingInfo,
    ProfileType
} from "../models/ProfileResponse";
import moment from "moment";
import { Condition, ConditionResultResponse } from "../../Monitoring/ConditionResultResponse";
import HeaderIcon from "./HeaderIcon";
import { formatCurrency } from "../../utils/format";
import { ClientType } from "../constants";
import { LegalAgreementsWithoutProfiles } from "../models/PartnerResource";
import { ProfileProposalActionMenu } from "../ClientProfile/ProfileDetails/ProfileProposalActionMenu";
import CustomModal from "../../components/Modal/Custom/CustomModal";
import { useAppInsights } from "src/AppInsights";
import { releaseToggleApiClient } from "../../ReleaseToggles/ReleaseToggleApiClient";
import LoadingIndicator from "../../pages/LoadingIndicator";
import CreateProfileModal from "./CreateProfileModal";
import { assetsApiClient } from "../../Assets/AssetsApiClient";
import { InvestmentProgramRestoreDto } from "../../Assets/models/InvestmentProgram";
import { initialProfileInfoState, selectRestoredProfileInfoState, setRestoredProfileInfoState } from "./ProfileIPInfoSlice";
import AlertBanner from "../../components/AlertBanner/AlertBanner";
import ConfirmationModalForIPRestore from "../../components/Modal/Confirmation/ConfirmationModalForIPRestore";
import {
    selectPartnerDashboardProfilesSortingInfoState,
    setPartnerDashboardProfilesSortingInfoState
} from "./ProfileSortingInfoSlice";
import { msalUtils } from "../../MsalUtils";
import { useMsal } from "@azure/msal-react";
import { partnerApiClient } from "../PartnerApiClient";
import { selectReleaseToggles } from "src/ReleaseToggles/releaseTogglesSlice";
import { selectIsNationalSampleAdmin } from "../../Authorization/nationalSampleAdminSlice";
import {
    selectSampleWasRecentlyDuplicated,
    setSampleWasRecentlyDuplicated,
    setShowDuplicateSuccessToast,
} from "./RecentlyDuplicatedSampleSlice";
import {duplicateNationalSample} from "../duplicateNationalSample";

export interface IPartnerDashboard {
    profiles: PartnerDashboardProfile[];
    profileConditions: ConditionResultResponse[];
    isLoading: boolean;
    tabType: ClientType;
    handleUnlinkedModal: () => void;
    handleRestoreArchivedProfile: (investmentProgramRestoreDto: InvestmentProgramRestoreDto) => void;
    enableNationalSamples?: boolean;
    nationalProfiles?: PartnerDashboardProfile[];
    onRefreshProfiles?: () => void;
}

interface DashboardSearchProps {
    id: string;
    name: string;
    value: string;
    onSearch: (search: string) => void;
}

const DashboardSearch = ({ id, name, value, onSearch }: DashboardSearchProps) => (
    <Search
        id={id}
        name={name}
        size="medium"
        onClearListener={() => onSearch("")}
        placeholder="Type to search..."
        handleChangeVal={(e: React.ChangeEvent<HTMLInputElement>) => onSearch(e.target.value)}
        value={value}
    />
);

const PartnerDashboard = ({
    profiles,
    profileConditions,
    isLoading,
    tabType,
    handleUnlinkedModal,
    handleRestoreArchivedProfile,
    enableNationalSamples,
    nationalProfiles,
    onRefreshProfiles,
}: IPartnerDashboard) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const msal = useMsal();
    const isNationalSampleAdmin = useAppSelector(selectIsNationalSampleAdmin);
    const sampleWasRecentlyDuplicated = useAppSelector(selectSampleWasRecentlyDuplicated);

    const hasAppliedRecentDuplicateSortRef = React.useRef(false);

    const [openArchiveNationalSampleModal, setOpenArchiveNationalSampleModal] = useState(false);
    const [keyForNationalSampleToBeArchived, setKeyForNationalSampleToBeArchived] = useState('');
    const [restoredNationalSampleName, setRestoredNationalSampleName] = useState('');
    const [originalProfiles, setOriginalProfiles] = useState([] as PartnerDashboardProfile[]);
    const [partnerDashboardProfiles, setPartnerDashboardProfiles] = useState([] as PartnerDashboardProfile[]);
    const [nationalSampleProfiles, setNationalSampleProfiles] = useState(nationalProfiles);
    const [partnerProfiles, setPartnerProfiles] = useState([] as string[]);
    const [conditionResultResponse, setConditionResultResponse] = useState([] as ConditionResultResponse[]);

    const [filteredLegalAgreements, setFilteredLegalAgreements] = useState([] as LegalAgreementsWithoutProfiles[]);
    const [legalAgreementsWithoutProfiles, setLegalAgreementsWithoutProfiles] = useState([] as LegalAgreementsWithoutProfiles[]);

    const [sortType, setSortType] = useState(SortType.descendingProfileConditions);

    const [lastSortType, setLastSortType] = useState(SortType.descendingProfileConditions);

    const [sortTypeLegalAgreement, setSortTypeLegalAgreement] = useState(SortType.ascendingProfileName);

    const [lastSortTypeLegalAgreement, setLastSortTypeLegalAgreement] = useState(SortType.ascendingProfileName);

    const [sortColumnName, setSortColumnName] = useState("profileAlerts");

    const [sortLegalAgreementColumn, setSortLegalAgreementColumn] = useState("profileName");

    const [searchText, setSearchText] = useState("");
    const [clientSubset, setClientSubset] = useState<FilterProfileOptions>("activeClients");
    const [conditionFilters, setConditionFilters] = useState(new Set() as Set<string>);
    const [legalAgreementFilters,] = useState(new Set() as Set<string>);
    const [dropdownKey, setDropdownKey] = useState('dropdownKey');

    const [openArchiveClientModal, setOpenArchiveClientModal] = useState(false);
    const [keyForProfileToBeArchived, setKeyForProfileToBeArchived] = useState('');

    const [openConvertToClientModal, setOpenConvertToClientModal] = useState(false);
    const [keyForProfileToBeConverted, setKeyForProfileToBeConverted] = useState('');
    const [enableCustomProfile, setEnableCustomProfile] = useState<boolean>(true);
    const [isFetching, setIsFetching] = useState<boolean>(true);
    const [openCustomModal, setOpenCustomModal] = useState<boolean>(false);
    const [nationalFilterOption, setNationalFilterOption] = useState<NationalSampleFilterOption>('activeClients');
    const [nationalSearchText, setNationalSearchText] = useState('');
    const [isDuplicatingNationalSample, setIsDuplicatingNationalSample] = useState(false);

    const restoredProfileInfo = useAppSelector(selectRestoredProfileInfoState)

    const profilesSortingInfo = useAppSelector(selectPartnerDashboardProfilesSortingInfoState)
    const [isLegalAgreementsLoading, setIsLegalAgreementsLoading] = useState(true);

    const releaseToggles: any = useAppSelector(selectReleaseToggles);
    const shouldShowNationalSamples = tabType === ClientType.SAMPLE && enableNationalSamples;
    const shouldShowNationalSamplesFilter = shouldShowNationalSamples && isNationalSampleAdmin;
    const shouldAddSplitSamplesEmptySpacing = shouldShowNationalSamples && partnerDashboardProfiles.length === 0;

    let openRestoreClientInfoModalInitialValue = false
    if (restoredProfileInfo.profileName !== null && restoredProfileInfo.profileOwner !== null) {
        openRestoreClientInfoModalInitialValue = true;
    }

    let openRestoreClientInfoModalForInvalidIPStatusInitialValue = false
    if (restoredProfileInfo.ipStatus === "Invalid") {
        openRestoreClientInfoModalForInvalidIPStatusInitialValue = true;
    }

    let showProfileRestoreBannerInitialValue = false;
    if (restoredProfileInfo.restoredProfileName !== "") {
        showProfileRestoreBannerInitialValue = true;
    }

    const [openRestoreClientInfoModal, setOpenRestoreClientInfoModal] = useState(openRestoreClientInfoModalInitialValue);

    const [openRestoreClientInfoModalForInvalidIPStatus, setOpenRestoreClientInfoModalForInvalidIPStatus] = useState(openRestoreClientInfoModalForInvalidIPStatusInitialValue);
    const [showProfileRestoreBanner, setShowProfileRestoreBanner] = useState<boolean>(showProfileRestoreBannerInitialValue)


    let profileType: ProfileType;
    if (tabType === ClientType.CLIENT) {
        profileType = ProfileType.CLIENT;
    } else if (tabType === ClientType.PROSPECT) {
        profileType = ProfileType.PROSPECT;
    } else {
        profileType = ProfileType.SAMPLE;
    }
    let dropdownItemName: DropdownItemNames;
    if (clientSubset === "activeClients") {
        dropdownItemName = DropdownItemNames.ACTIVE_CLIENT
    } else if (clientSubset === "archivedClients") {
        dropdownItemName = DropdownItemNames.ARCHIVED_CLIENTS
    } else if (clientSubset === "clientsWithConditions") {
        dropdownItemName = DropdownItemNames.CLIENTS_WITH_CONDITIONS;
    } else {
        dropdownItemName = DropdownItemNames.LEGAL_AGREEMENTS_WP;
    }

    const defaultFilterSettings = {
        searchText: searchText,
        clientSubset: clientSubset,
        conditionFilters: conditionFilters,
        legalAgreementFilters: legalAgreementFilters,
    }

    const handleNewCustomClientNavigation = (text: string) => {
        const newClientProfile = `/Profile/${text}/New`;
        if (text === 'Client' && enableCustomProfile) {
            history.push(releaseToggles?.enableOneIntegration ? "/ClientRelationshipGroup" : "/PartnerWorkstation")
        } else {
            history.push(newClientProfile);
        }
        dispatch(setProfileRequestForm(getEmptyProfileRequestForm()))
        dispatch(setNewClientFormInteractions(getEmptyNewClientFormInteractions()))
        dispatch(setIsSaveButtonClicked(false))
    };

    const handleCreateNewProfile = () => {
        clientManagementApiClient.postProfileSortingInfo(profilesSortingInfo);
        if (enableCustomProfile) {
            setOpenCustomModal(true);
        } else {
            handleNewCustomClientNavigation(tabFormattedText(tabType, enableCustomProfile).routeName);
        }
    };

    const renderCreateNewProfileButton = (id: string) => (
        <Col>
            <div className="newClientId">
                <Button
                    id={id}
                    className="new-client-button"
                    icon="none"
                    iconName="settings"
                    includeRef={false}
                    kind="primary"
                    size="medium"
                    tabIndex={0}
                    type="button"
                    onClick={handleCreateNewProfile}>
                    <span className="new-client-text">
                        {tabFormattedText(tabType, enableCustomProfile).createNewButton}
                    </span>
                </Button>
            </div>
        </Col>
    );

    const handleLegalAgreementSorting = (column: Column, sorting: "ascending" | "descending") => {
        setSortLegalAgreementColumn(column.id);

        const sortConfig = sortLAMapping[column.id];
        if (sortConfig) {
            setSortTypeLegalAgreement(sorting === "ascending" ? sortConfig.ascending : sortConfig.descending);
            setLastSortTypeLegalAgreement(sorting === "ascending" ? sortConfig.ascending : sortConfig.descending);
        }
    };

    const handleSorting = (column: Column, sorting: "ascending" | "descending") => {
        setSortColumnName(column.id);

        const sortConfig = sortClientMapping[column.id];
        if (sortConfig) {
            setSortType(sorting === "descending" ? sortConfig.descending : sortConfig.ascending);
            setLastSortType(sorting === "descending" ? sortConfig.descending : sortConfig.ascending);
        }
    };

    const hasProfilesWithConditions = clientSubset === "clientsWithConditions";
    const hasLegalAgreementsWithoutProfile = clientSubset === 'legalAgreementsWithoutProfile';

    const filterLegalAgreementsOptionDropdown = (queryParams: typeof defaultFilterSettings) => {
        return legalAgreementsWithoutProfiles.filter((legalAgreements) => legalAgreements.profileName
            .toLowerCase()
            .includes(queryParams.searchText.toLowerCase()) || legalAgreements.legalAgreementName
                .toLowerCase()
                .includes(queryParams.searchText.toLowerCase())
        );
    };

    const applySearchTextFilter = (dashboardProfiles: PartnerDashboardProfile[], searchQuery: string) => {
        return dashboardProfiles.filter((profile) => profile.displayName
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        );
    }

    const filterOnProfileOptionsDropdown = (queryParams: typeof defaultFilterSettings) => {

        if (queryParams.clientSubset === 'archivedClients') {
            return applySearchTextFilter(filterOnlyArchivedClients(originalProfiles), queryParams.searchText)
        }

        const activeProfilesFilteredbyQuery = applySearchTextFilter(filterOnlyActiveProfiles(originalProfiles), queryParams.searchText)

        if (queryParams.clientSubset === 'clientsWithConditions') {
            let clientsWithConditionsWithAndWithoutDaysActive: PartnerDashboardProfile[] = [];
            activeProfilesFilteredbyQuery.forEach(profile => {

                let conditionResultResponseData = conditionResultResponse.find(conditionResult => conditionResult.profileId === profile.id);
                if (conditionResultResponseData) {
                    let activeDaysWithConditions = Math.max(...conditionResultResponseData.conditions.map(v => v.activeDays))
                    clientsWithConditionsWithAndWithoutDaysActive.push({ ...profile, daysActive: activeDaysWithConditions });
                }
                else {
                    clientsWithConditionsWithAndWithoutDaysActive.push(profile);
                }

            })
            let clientsWithConditionsData = clientsWithConditionsWithAndWithoutDaysActive
                .filter(profile => {
                    const conditionsInProfile = conditionResultResponse.find(condition => condition.profileId === profile.id && condition.conditions.length > 0)
                    if (!conditionsInProfile) return false;

                    if (queryParams.conditionFilters.size === 0) return true;

                    return Array.from(queryParams.conditionFilters)
                        .find((conditionInFilter) => conditionsInProfile.conditions.map((c) => c.conditionName.toLowerCase())
                            .includes(conditionInFilter.toLowerCase())
                        );
                });
            return clientsWithConditionsData;
        } else {
            return activeProfilesFilteredbyQuery;
        }
    };

    const handleSearch = (search: string) => {
        setSearchText(search);
        if (hasLegalAgreementsWithoutProfile) {
            setFilteredLegalAgreements(filterLegalAgreementsOptionDropdown({
                ...defaultFilterSettings,
                searchText: search
            }));
        } else {
            setPartnerDashboardProfiles(filterOnProfileOptionsDropdown({ ...defaultFilterSettings, searchText: search }));
        }

    };

    const handleNationalSearch = (search: string) => {
        setNationalSearchText(search);
        const source = nationalProfiles ?? [];
        const filtered = source.filter((profile) =>
            profile.displayName.toLowerCase().includes(search.toLowerCase())
        );
        setNationalSampleProfiles(filtered);
    };

    useEffect(() => {
        const query = nationalSearchText.toLowerCase();
        setNationalSampleProfiles((nationalProfiles ?? []).filter(profile =>
            profile.displayName.toLowerCase().includes(query)
        ));
    }, [nationalProfiles, nationalSearchText]);

    const handleDropdown = (viewOption: FilterProfileOptions) => {
        setClientSubset(viewOption);
        if (viewOption === 'legalAgreementsWithoutProfile') {
            setSortTypeLegalAgreement(SortType.ascendingProfileName)
            setLastSortTypeLegalAgreement(SortType.ascendingProfileName)
            setFilteredLegalAgreements(legalAgreementsWithoutProfiles);
        } else {
            setSortType(SortType.descendingProfileConditions)
            setLastSortType(SortType.descendingProfileConditions)
            setPartnerDashboardProfiles(filterOnProfileOptionsDropdown({
                ...defaultFilterSettings,
                clientSubset: viewOption
            }));
        }
    };

    const updateWithLastModified = (partnerDashboardProfile: PartnerDashboardProfile[]) => {
        return sortProfiles(sortType,
            partnerDashboardProfile.map((profile) => {
                return profile.lastModifiedDate
                    ? profile
                    : {
                        ...profile,
                        lastModifiedDate: profile.createdDate,
                    };
            })
        );
    }

    const getProfilesForSavedSort = () => {
        if (clientSubset === "activeClients") {
            return filterOnlyActiveProfiles(profiles);
        }
        if (clientSubset === "archivedClients") {
            return filterOnlyArchivedClients(profiles);
        }
        if (clientSubset === "clientsWithConditions") {
            return filterOnProfileOptionsDropdown({
                ...defaultFilterSettings,
                clientSubset: "clientsWithConditions"
            });
        }
        return [];
    };

    const applySavedProfileSorting = (profileSortingInfoFromDb: PartnerDashboardProfilesSortingInfo) => {
        if (profileSortingInfoFromDb.lastSortType) {
            setLastSortType(lastModifiedSortType(profileSortingInfoFromDb.lastSortType.replace(/ /g, ''))!)
            setSortColumnName(profileSortingInfoFromDb.sortColumnName.replace(/ /g, ''))
        }
        setSortType(SortType.ascendingOrdinal);

        const profilesToBeSorted = getProfilesForSavedSort().map(profile => {
            const profileSortingInfo = profileSortingInfoFromDb.profileSortingInfo.find(profileSortingInfoDb =>
                profile.id.replace(/ /g, '') === profileSortingInfoDb.profileOrLegalAgreementId.replace(/ /g, '')
            );
            return {
                ...profile,
                ordinal: profileSortingInfo?.ordinal ?? profileSortingInfoFromDb.profileSortingInfo.length
            };
        });
        const sortedProfiles = sortProfiles(SortType.ascendingOrdinal, profilesToBeSorted);

        setOriginalProfiles([...profiles]);
        setPartnerDashboardProfiles([...sortedProfiles]);
        setPartnerProfiles(filterOnlyActiveProfileId(updateWithLastModified(profiles)));
    };

    const applyDefaultProfileSorting = (defaultSort: SortType, resetRecentlyDuplicatedSample = false) => {
        if (clientSubset !== "activeClients") return;

        setSortType(defaultSort);
        setLastSortType(defaultSort)
        if (resetRecentlyDuplicatedSample) {
            setSortColumnName('lastModifiedDate');
        }

        const sortedProfiles = updateWithLastModified(profiles);
        setOriginalProfiles([...sortedProfiles]);
        setPartnerDashboardProfiles([...filterOnlyActiveProfiles(sortedProfiles)]);
        setPartnerProfiles(filterOnlyActiveProfileId(updateWithLastModified(profiles)));
        setClientSubset(clientSubset);
        setDropdownKey(dropdownKey + 1)
    };

    useEffect(() => {
        const lanId = msalUtils.getLanId(msal);
        if (clientSubset === "legalAgreementsWithoutProfile") return;

        const resetRecentlyDuplicatedSample = Boolean(
            shouldShowNationalSamples && sampleWasRecentlyDuplicated
        );

        clientManagementApiClient.getProfileSortingInfo(lanId, profileType, dropdownItemName)
            .then((profileSortingInfoFromDb) => {
                const hasSavedSorting = profileSortingInfoFromDb.lanID !== ""
                    && profileSortingInfoFromDb.profileSortingInfo.length > 0;

                if (hasSavedSorting && !resetRecentlyDuplicatedSample) {
                    applySavedProfileSorting(profileSortingInfoFromDb);
                    return;
                }

                const defaultSort = resetRecentlyDuplicatedSample
                    ? SortType.descendingLastModifiedDate
                    : SortType.descendingProfileConditions;
                applyDefaultProfileSorting(defaultSort, resetRecentlyDuplicatedSample);

                if (resetRecentlyDuplicatedSample) {
                    hasAppliedRecentDuplicateSortRef.current = true;
                }
            })
            .catch(() => {
                const defaultSort = resetRecentlyDuplicatedSample
                    ? SortType.descendingLastModifiedDate
                    : SortType.descendingProfileConditions;
                applyDefaultProfileSorting(defaultSort, resetRecentlyDuplicatedSample);

                if (resetRecentlyDuplicatedSample) {
                    hasAppliedRecentDuplicateSortRef.current = true;
                }
            });
    }, [profiles, clientSubset]);

    useEffect(() => {
        if (
            shouldShowNationalSamples
            && hasAppliedRecentDuplicateSortRef.current
            && sampleWasRecentlyDuplicated
        ) {
            hasAppliedRecentDuplicateSortRef.current = false;
            dispatch(setSampleWasRecentlyDuplicated(false));
        }
    }, [
        dispatch,
        sampleWasRecentlyDuplicated,
        sortType,
        shouldShowNationalSamples,
    ]);

    useEffect(() => {
        if (partnerProfiles.length > 0) {
            setConditionResultResponse(profileConditions.filter(profileCondition => partnerProfiles.includes(profileCondition.profileId)));
        }
    }, [partnerProfiles]);

    useEffect(() => {
        if (clientSubset === 'legalAgreementsWithoutProfile') {
            setIsLegalAgreementsLoading(true);
            partnerApiClient.getLegalAgreementsWithoutProfile()
                .then((legalAgreementResults) => {
                    const lanId = msalUtils.getLanId(msal);
                    let sortedLegalAgreements: LegalAgreementsWithoutProfiles[] = [];
                    clientManagementApiClient.getProfileSortingInfo(lanId, profileType, dropdownItemName)
                        .then((profileSortingInfoFromDb) => {
                            if (profileSortingInfoFromDb.lanID !== "" && profileSortingInfoFromDb.profileSortingInfo.length > 0) {

                                if (profileSortingInfoFromDb.lastSortType) {
                                    setLastSortTypeLegalAgreement(lastModifiedSortTypeLegalAgreement(profileSortingInfoFromDb.lastSortType.replace(/ /g, ''))!)
                                    setSortLegalAgreementColumn(profileSortingInfoFromDb.sortColumnName.replace(/ /g, ''))
                                }
                                setSortTypeLegalAgreement(SortType.ascendingOrdinal);
                                let legalAgreementsToBeSorted: LegalAgreementsWithoutProfiles[] = [];

                                let legalAgreementsWithoutProfiles: LegalAgreementsWithoutProfiles[] = [];

                                legalAgreementsWithoutProfiles = [...legalAgreementResults]
                                legalAgreementsWithoutProfiles.forEach(legalAgreementsWithoutProfile => {

                                    const legalAgreementInfo = profileSortingInfoFromDb.profileSortingInfo.find(profileSortingInfoDb => {
                                        return legalAgreementsWithoutProfile.legalAgreementId.replace(/ /g, '') === profileSortingInfoDb.profileOrLegalAgreementId.replace(/ /g, '')
                                    })
                                    if (legalAgreementInfo) {
                                        legalAgreementsToBeSorted.push({
                                            ...legalAgreementsWithoutProfile,
                                            ordinal: legalAgreementInfo.ordinal
                                        })
                                    } else {
                                        legalAgreementsToBeSorted.push({
                                            ...legalAgreementsWithoutProfile,
                                            ordinal: profileSortingInfoFromDb.profileSortingInfo.length
                                        })
                                    }

                                })

                                sortedLegalAgreements = sortLegalAgreements(SortType.ascendingOrdinal,
                                    legalAgreementsToBeSorted
                                );
                                setLegalAgreementsWithoutProfiles(sortedLegalAgreements);
                                setFilteredLegalAgreements(sortedLegalAgreements);
                                setIsLegalAgreementsLoading(false);
                            }
                            else {
                                setLegalAgreementsWithoutProfiles(legalAgreementResults);
                                setFilteredLegalAgreements(legalAgreementResults);
                                setIsLegalAgreementsLoading(false);
                            }

                        })
                        .catch((error) => {
                            setLegalAgreementsWithoutProfiles(legalAgreementResults || []);
                            setFilteredLegalAgreements(legalAgreementResults || []);
                            setIsLegalAgreementsLoading(false);
                        });
                })
                .catch((error) => {
                    console.error('Failed to retrieve Legal Agreements without a Profile', error.message);
                    setLegalAgreementsWithoutProfiles([]);
                    setFilteredLegalAgreements([]);
                    setIsLegalAgreementsLoading(false);
                })
        }
    }, [clientSubset]);

    useEffect(() => {
        setIsFetching(true)
        releaseToggleApiClient.getLaunchDarklyFlag("enableCustomProfile").then(
            (enableCustomProfileResponse) => {
                setEnableCustomProfile(enableCustomProfileResponse);
                setIsFetching(false)
            }
        ).catch((error) => {
            console.error('Failed to retrieve enableCustomProfile', error.message);
        });
    }, []);

    useMemo(() => {
        const lanId = msalUtils.getLanId(msal);
        if ((!tabType.includes(profilesSortingInfo.profileType.toLowerCase())) || (!clientSubset.toLowerCase().includes(profilesSortingInfo.dropdownItemName.replace(/ /g, '').toLowerCase()))) {
            clientManagementApiClient.postProfileSortingInfo(profilesSortingInfo);
        }
        let partnerDashboardProfilesData: PartnerDashboardProfile[] = sortProfiles(sortType, partnerDashboardProfiles, profileConditions)
        setPartnerDashboardProfiles(partnerDashboardProfilesData);

        let profileSortingInfoObj: ProfileSortingInfo[] = [];
        partnerDashboardProfilesData.forEach((item, index) => {
            profileSortingInfoObj.push({
                profileOrLegalAgreementId: item.id,
                ordinal: index
            })
        });

        dispatch(setPartnerDashboardProfilesSortingInfoState({
            lanID: lanId,
            profileType: profileType,
            dropdownItemName: dropdownItemName,
            profileSortingInfo: profileSortingInfoObj,
            lastSortType: lastSortType,
            sortColumnName: sortColumnName
        }));
    }, [sortType, partnerDashboardProfiles]);

    useMemo(() => {
        const lanId = msalUtils.getLanId(msal);
        if ((!tabType.includes(profilesSortingInfo.profileType.toLowerCase())) || (!clientSubset.toLowerCase().includes(profilesSortingInfo.dropdownItemName.replace(/ /g, '').toLowerCase()))) {
            if (dropdownItemName === DropdownItemNames.LEGAL_AGREEMENTS_WP) {
                clientManagementApiClient.postProfileSortingInfo(profilesSortingInfo);
            }
        }
        let legalAgreementsWithoutProfilesData: LegalAgreementsWithoutProfiles[] = sortLegalAgreements(sortTypeLegalAgreement, filteredLegalAgreements)
        setFilteredLegalAgreements(legalAgreementsWithoutProfilesData);

        let profileSortingInfoObj: ProfileSortingInfo[] = [];
        legalAgreementsWithoutProfilesData.forEach((item, index) => {
            profileSortingInfoObj.push({
                profileOrLegalAgreementId: item.legalAgreementId,
                ordinal: index
            })
        });

        dispatch(setPartnerDashboardProfilesSortingInfoState({
            lanID: lanId,
            profileType: profileType,
            dropdownItemName: dropdownItemName,
            profileSortingInfo: profileSortingInfoObj,
            lastSortType: lastSortTypeLegalAgreement,
            sortColumnName: sortLegalAgreementColumn
        }));

    }, [sortTypeLegalAgreement, filteredLegalAgreements])

    function filterOnlyActiveProfiles(profilesToFilter: PartnerDashboardProfile[]): PartnerDashboardProfile[] {
        return profilesToFilter.filter(p => !p.archived && p.activeClient)
    }

    function filterOnlyArchivedClients(profilesToFilter: PartnerDashboardProfile[]): PartnerDashboardProfile[] {
        return profilesToFilter.filter(p => !p.activeClient && !p.archived)
    }

    function filterOnlyActiveProfileId(profilesToFilter: PartnerDashboardProfile[]): string[] {
        return profilesToFilter.map(p => p.id);
    }

    function restoreArchivedClientProfile(partnerDashboardProfile: PartnerDashboardProfile) {
        clientManagementApiClient.putPartnerDashboardProfile({
            ...partnerDashboardProfile,
            archived: false,
            activeClient: true
        }).then(() => {
            assetsApiClient.restoreInvestmentProgram(partnerDashboardProfile.id).then(response => {
                setClientSubset('activeClients');
                setDropdownKey(dropdownKey + 1)
                handleRestoreArchivedProfile(response);
                setShowProfileRestoreBanner(true)
                dispatch(setRestoredProfileInfoState({
                    ...response,
                    restoredProfileName: partnerDashboardProfile.displayName
                }));
                if (response.profileName !== null && response.profileOwner !== null) {
                    setOpenRestoreClientInfoModal(true);
                } else if (response.ipStatus === "Invalid") {
                    setOpenRestoreClientInfoModalForInvalidIPStatus(true)
                }

            }).catch(error => {
                console.error('Failed to restore investment program', error.message);
            });
        });
    }

    function ProfileMenu(partnerDashboardProfile: PartnerDashboardProfile) {
        const openArchiveModalForProfile = () => {
            setKeyForProfileToBeArchived(partnerDashboardProfile.id)
            setOpenArchiveClientModal(true)
        };

        const createArchiveDropdownGroup = (ariaLabel: string, itemText: string, value: string) => (
            <DropdownGroup key="group-archiveClient" groupName="profileActionMenu">
                <DropdownItem
                    key={`archiveClient ${partnerDashboardProfile.id}`}
                    aria-label={ariaLabel}
                    onClick={openArchiveModalForProfile}
                    itemText={itemText}
                    value={value}
                />
            </DropdownGroup>
        );

        function navigateToProfileDetails() {
            history.push({
                pathname: `/Profile/${partnerDashboardProfile.id}`,
            });
        }

        const partnerDashboardDropdownGroups = [
            <DropdownGroup key="group-viewProfileDetails" groupName="profileActionMenu">
                <DropdownItem
                    key={`viewProfilesDetails ${partnerDashboardProfile.id}`}
                    aria-label="viewProfilesDetails"
                    className="viewProfile"
                    onClick={() => navigateToProfileDetails()}
                    itemText="View Profile Details"
                    value="ViewProfile"
                />
            </DropdownGroup>
        ];

        const activeProspectDropdownGroup =
            <DropdownGroup key="group-archiveClient" groupName="profileActionMenu">
                <DropdownItem
                    key={`convertToClient ${partnerDashboardProfile.id}`}
                    aria-label="convertToClient"
                    onClick={() => {
                        setKeyForProfileToBeConverted(partnerDashboardProfile.id)
                        setOpenConvertToClientModal(true)
                    }}
                    itemText={"Convert to Client"}
                    value={"ConvertToClient"}
                />
                <DropdownItem
                    key={`archiveClient ${partnerDashboardProfile.id}`}
                    aria-label="archiveClient"
                    onClick={openArchiveModalForProfile}
                    itemText={partnerDashboardProfile.prospect ? "Archive Prospect" : "Archive Client"}
                    value="ArchiveClient"
                />
            </DropdownGroup>

        const activeProfileDropdownGroup = createArchiveDropdownGroup(
            "archiveClient",
            partnerDashboardProfile.prospect ? "Archive Prospect" : "Archive Client",
            "ArchiveClient"
        );

        const activeSampleDropdownGroup = createArchiveDropdownGroup(
            "archiveSample",
            partnerDashboardProfile.sample ? "Archive Sample" : "Archive Client",
            "ArchiveSample"
        );

        if (clientSubset === "activeClients") {
            if (partnerDashboardProfile.prospect) {
                partnerDashboardDropdownGroups.push(activeProspectDropdownGroup);
            } else if (partnerDashboardProfile.sample) {
                partnerDashboardDropdownGroups.push(activeSampleDropdownGroup);
            } else {
                partnerDashboardDropdownGroups.push(activeProfileDropdownGroup)
            }
        }
        const archivedClientDropdownGroup =
            <DropdownGroup key="group-restoreClient" groupName="profileActionMenu">
                <DropdownItem
                    key={`restoreClient ${partnerDashboardProfile.id}`}
                    aria-label="restoreClient"
                    onClick={() => restoreArchivedClientProfile(partnerDashboardProfile)}
                    itemText={getProfileType(partnerDashboardProfile)}
                    value="RestoreClient"
                />
            </DropdownGroup>
        if (clientSubset === "archivedClients") partnerDashboardDropdownGroups.push(archivedClientDropdownGroup);

        return (
            <ProfileProposalActionMenu
                dropdownItems={partnerDashboardDropdownGroups}
            />
        );
    }

    function getProfileType(partnerDashboardProfile: PartnerDashboardProfile) {
        let profileType;
        if (partnerDashboardProfile.prospect) {
            profileType = "Restore Prospect";
        } else if (partnerDashboardProfile.sample) {
            profileType = "Restore Sample";
        } else {
            profileType = "Restore Client";
        }
        return profileType;
    }

    function loadProfileStatus() {
        if (isLoading) return null;

        if (originalProfiles.length === 0) {
            return `You have no ${tabFormattedText(tabType, enableCustomProfile).noMatchingText} available.`
        } else {
            if (clientSubset === 'archivedClients' && searchText.length === 0) {
                return `No ${tabFormattedText(tabType, enableCustomProfile).noArchivedMatchingText} have been archived.`
            } else if (clientSubset === 'legalAgreementsWithoutProfile') {
                if (!isLegalAgreementsLoading && filteredLegalAgreements.length === 0) {
                    return `No matching ${tabFormattedText(tabType, enableCustomProfile).noMatchingText} could be found.`
                }
                return null;
            }
            return `No matching ${tabFormattedText(tabType, enableCustomProfile).noMatchingText} could be found.`
        }
    }

    const renderSortDropdown = (
        title: string,
        sortType: string,
        columnName: string,
        isLegalAgreement?: boolean
    ) => {
        return (
            <div className={`display-flex`}>
                <div>
                    <HeaderIcon
                        title={title}
                        sortType={sortType}
                    />
                </div>
                <div className={`dropdown-align`}>
                    <Dropdown
                        alignRight={true}
                        dropdownKind="menu"
                        iconNameOpen="menu_dots"
                        iconNameClose="menu_dots"
                        className="sort-dropdown"
                        buttonIcon={"only"}
                        buttonKind="borderless"
                        buttonSize={"small"}
                        rounded={false}
                        panelWidth={180}
                    >
                        <DropdownItem
                            aria-label="Sort Ascending"
                            value="Sort Ascending"
                            onClick={() => {
                                (isLegalAgreement && columnName !== "clientId") ? handleLegalAgreementSorting({ id: columnName }, "ascending") :
                                    handleSorting({ id: columnName }, "ascending");
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>Sort Ascending</div>
                                {sortType.includes("ascending") && (
                                    <div>
                                        <i className="dds-icons icon--size-14">✔</i>
                                    </div>
                                )}
                            </div>
                        </DropdownItem>
                        <DropdownItem
                            aria-label="Sort Descending"
                            value="Sort Descending"
                            onClick={() => {
                                (isLegalAgreement && columnName !== "clientId") ? handleLegalAgreementSorting({ id: columnName }, "descending") :
                                    handleSorting({ id: columnName }, "descending");
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>Sort Descending</div>
                                {sortType.includes("descending") && (
                                    <div>
                                        <i className="dds-icons icon--size-14">✔</i>
                                    </div>
                                )}
                            </div>
                        </DropdownItem>
                    </Dropdown>
                </div>
            </div>
        );
    };

    const clientsOrProspectsPageColumns = [
        {
            Header: () => (
                renderSortDropdown(
                    "Name",
                    sortColumnName === "displayName" ? lastSortType : "",
                    "displayName"
                )
            ),
            accessor: "displayName",
            Cell: ({
                original,
            }: {
                original: PartnerDashboardProfile;
            }) => (
                <span data-testid="display-name" className="display-flex align-items-center">
                    <Link
                        className="table-displayName"
                        role="link"
                        to={`/Profile/${original.id}`}
                    >
                        {original.displayName}
                    </Link>
                    {
                        original.activeClient && !original.prospect && !original.sample && !original.archived && !hasProfilesWithConditions && !original.resourceCode ?
                            <Button className="up-popover-button unlinked-profile"
                                data-testid="unlink-profile-testid"
                                icon="only"
                                iconName="link_off"
                                kind="borderless"
                                size="small"
                                onClick={handleUnlinkedModal} /> : <></>}
                </span>
            ),
            minWidth: 155,
        },

        {
            Header: () => (
                renderSortDropdown(
                    "Portfolio Reserve",
                    sortColumnName === "portfolioReserve" ? lastSortType : "",
                    "portfolioReserve"
                )
            ),
            accessor: "portfolioReserve",
            show: tabType === ClientType.CLIENT && clientSubset !== "archivedClients",
            Cell: ({
                original,
            }: {
                original: PartnerDashboardProfile;
            }) => (
                <span data-testid="portfolio-reserve" className="display-flex align-items-center ">
                    {original.activePortfolioReserve ?
                        <><span style={{ backgroundColor: "#05676e", marginRight: "6px" }}
                            className="portfolio-reserve-status-bullet" /> <span
                                className="paddingleft-sm">ON</span></>
                        :
                        <><span style={{ backgroundColor: "grey", marginRight: "6px" }}
                            className="portfolio-reserve-status-bullet" />
                            <span className="paddingleft-sm">OFF</span></>
                    }
                </span>
            ),
            width: 151,
        },

        {
            Header: () => (
                renderSortDropdown(
                    "Annual Report Last Sent",
                    sortColumnName === "annualReportSentDate" ? lastSortType : "",
                    "annualReportSentDate"
                )
            ),
            accessor: "annualReportSentDate",
            show: tabType === ClientType.CLIENT && clientSubset !== "archivedClients",
            Cell: ({
                original,
            }: {
                original: PartnerDashboardProfile;
            }) => (
                <span data-testid="annual-report" className="display-flex align-items-center paddingleft-xxxl">
                    {original.annualReportSentDate ?
                        <span
                            className="paddingleft-sm">{toDisplayDateFormat(original.annualReportSentDate)}</span>
                        :
                        <></>
                    }
                </span>
            ),
            width: 188,
        },

        {
            Header: () => (
                renderSortDropdown(
                    "Days Active",
                    sortColumnName === "daysActive" ? lastSortType : "",
                    "daysActive"
                )
            ),
            show: hasProfilesWithConditions,
            className: "days-active",
            accessor: "daysActive",
            Cell: ({
                original,
            }: {
                original: PartnerDashboardProfile;
            }) => (
                <span data-testid="days-active">
                    {conditionResultResponse.filter(
                        (conditionResult) =>
                            original.id === conditionResult.profileId)
                        .map((val) =>
                            <span key={val.profileId}>
                                {Math.max(...val.conditions.map(v => v.activeDays))} days
                            </span>
                        )}
                </span>
            ),
            width: 110,
        },
        {
            Header: () => (
                renderSortDropdown(
                    "Last Client Meeting",
                    sortColumnName === "lastMeetingEndDate" ? lastSortType : "",
                    "lastMeetingEndDate"
                )
            ),
            accessor: "lastMeetingEndDate",
            Cell: ({
                original,
            }: {
                original: PartnerDashboardProfile;
            }) => (
                <span data-testid="condition-number">
                    {toDisplayDateFormat(original.lastMeetingEndDate)}
                </span>
            ),
            width: 163,
        },
        {
            Header: () => (
                renderSortDropdown(
                    clientSubset === "archivedClients" ? "Archived On" : "Last Modified",
                    sortColumnName === "lastModifiedDate" ? lastSortType : "",
                    "lastModifiedDate"
                )
            ),
            accessor: "lastModifiedDate",
            Cell: ({
                original,
            }: {
                original: PartnerDashboardProfile;
            }) => (
                <span>
                    {toDisplayDateFormat(
                        original.lastModifiedDate
                    )}
                </span>
            ),
            width: 125,
        },
        {
            Header: () => (
                renderSortDropdown(
                    clientSubset === "archivedClients" ? "Archived By" : "Modified By",
                    sortColumnName === "lastModifiedByName" ? lastSortType : "",
                    "lastModifiedByName"
                )
            ),
            accessor: "lastModifiedByName",
            Cell: ({
                original,
            }: {
                original: PartnerDashboardProfile;
            }) => <span>{original.lastModifiedByName}</span>,
            width: 116,
        },
        {
            Cell: ({
                original,
            }: {
                original: PartnerDashboardProfile;
            }) => ProfileMenu(original),
            className: "overflow-visible",
            accessor: "clientId",
            minWidth: 32,
            maxWidth: 32,
        },
    ];

    const legalAgreementsPageColumns = [
        {
            Header: () => (
                renderSortDropdown(
                    "Name",
                    sortLegalAgreementColumn === "profileName" ? lastSortTypeLegalAgreement : "",
                    "profileName",
                    true
                )
            ),
            accessor: "profileName",
            Cell: ({
                original,
            }: {
                original: LegalAgreementsWithoutProfiles;
            }) => (
                <span data-testid="profile-name" className="display-flex align-items-center">
                    {original.profileId ?
                        <Link
                            className="table-profileName"
                            role="link"
                            to={`/Profile/${original.profileId}/ClientProfile/AssetSummary`}
                        >
                            {original.profileName}
                        </Link> : <i>{original.profileName}</i>
                    }
                </span>
            )
        },
        {
            Header: () => (
                renderSortDropdown(
                    "Legal Agreement",
                    sortLegalAgreementColumn === "legalAgreementName" ? lastSortTypeLegalAgreement : "",
                    "legalAgreementName",
                    true
                )
            ),
            accessor: "legalAgreementName",
            Cell: ({
                original,
            }: {
                original: LegalAgreementsWithoutProfiles;
            }) => (
                <span data-testid="agreement-name">
                    {original.legalAgreementName}
                </span>
            ),
            width: 400,
        },
        {
            Header: () => (
                renderSortDropdown(
                    "Legal Agreement ID",
                    sortLegalAgreementColumn === "legalAgreementId" ? lastSortTypeLegalAgreement : "",
                    "legalAgreementId",
                    true
                )
            ),
            accessor: "legalAgreementId",
            Cell: ({
                original,
            }: {
                original: LegalAgreementsWithoutProfiles;
            }) => (
                <span data-testid="agreement-id">
                    {original.legalAgreementId}
                </span>
            ),
            width: 200,
        },
        {
            Header: () => (
                renderSortDropdown(
                    "Days Active",
                    sortLegalAgreementColumn === "daysActive" ? lastSortTypeLegalAgreement : "",
                    "daysActive",
                    true
                )
            ),
            accessor: "daysActive",
            Cell: ({
                original,
            }: {
                original: LegalAgreementsWithoutProfiles;
            }) => (<span data-testid="days-active">{original.daysActive}</span>),
            width: 200,
        },
    ];

    if (tabType === ClientType.CLIENT) {
        clientsOrProspectsPageColumns.splice(2, 0, {
            Header: () => (
                renderSortDropdown(
                    "Profile Conditions",
                    sortColumnName === "profileAlerts" ? lastSortType : "",
                    "profileAlerts"
                )
            ),
            show: clientSubset !== "archivedClients",
            className: "profile-alerts",
            accessor: "profileAlerts",
            Cell: ({
                original,
            }: {
                original: PartnerDashboardProfile;
            }) => (
                <span data-testid="condition-number">
                    {conditionResultResponse.filter(
                        (conditionResult) =>
                            original.id === conditionResult.profileId)
                        .map((val) =>
                            val.conditions.length > 0 ?
                                <Badge value={val.conditions.length}
                                    data-testid="profile-condition"
                                    type={"error"}
                                    className="badge-condition-length"
                                    key={val.profileId} />
                                : null)}
                </span>
            ),
            width: 154,
        })
    }

    function getSubComponent() {
        if (hasProfilesWithConditions) {
            return ({ original }: { original: PartnerDashboardProfile }) => {
                return getSubComponentTable(original, conditionResultResponse)
            }
        }
        return null;
    }

    const handleArchiveClient = () => {
        setOpenArchiveClientModal(false)
        clientManagementApiClient.putPartnerDashboardProfile({
            ...(originalProfiles.filter(profile => profile.id === keyForProfileToBeArchived)[0]),
            id: keyForProfileToBeArchived,
            archived: false,
            activeClient: false
        }).then(() => {
            assetsApiClient.unlinkInvestmentProgramsForProfile(keyForProfileToBeArchived);
            window.location.reload();
        }).catch((error) => {
            console.error('Could not archive partner dashboard profile', error.message);
        });
        setKeyForProfileToBeArchived('')
    };

    const appInsights = useAppInsights();
    const handleConvertToClient = () => {
        setOpenConvertToClientModal(false);
        clientManagementApiClient.convertProspect(keyForProfileToBeConverted).then(() => {
            appInsights.trackEvent({
                name: 'ProspectConversion',
                properties: {
                    action: 'Prospect Converted',
                    profileId: keyForProfileToBeConverted,
                    data: 'Prospect converted to client in UI'
                }
            })
            history.push('/');
            window.location.reload()
        }).catch((error) => {
            console.error('Could not convert prospect to client', error.message);
        })
        setKeyForProfileToBeConverted('')
    }

    const closeProfileRestoreBanner = () => {
        dispatch(setRestoredProfileInfoState(initialProfileInfoState.investmentProgramRestoreInfo));
        setShowProfileRestoreBanner(false)
    };

    const handleDuplicateNationalSample = async (sampleId: string) => {
        await duplicateNationalSample({
            profileId: sampleId,
            isDuplicatingNationalSample,
            setIsDuplicatingNationalSample,
            onSuccess: () => {
                dispatch(setSampleWasRecentlyDuplicated(true));
                setSortType(SortType.descendingLastModifiedDate);
                setLastSortType(SortType.descendingLastModifiedDate);
                setSortColumnName('lastModifiedDate');
                dispatch(setShowDuplicateSuccessToast(true));
                onRefreshProfiles?.();
            },
        });
    };

    const handleArchiveNationalSample = (profileId: string) => {
        setKeyForNationalSampleToBeArchived(profileId);
        setOpenArchiveNationalSampleModal(true);
    };

    const confirmArchiveNationalSample = () => {
        setOpenArchiveNationalSampleModal(false);
        const profileToArchive = nationalProfiles?.find(p => p.id === keyForNationalSampleToBeArchived);
        if (profileToArchive) {
            clientManagementApiClient.putPartnerDashboardProfile({
                ...profileToArchive,
                activeClient: false,
                archived: false,
            }).then(() => {
                assetsApiClient.unlinkInvestmentProgramsForProfile(keyForNationalSampleToBeArchived);
                onRefreshProfiles?.();
            }).catch(error => console.error('Could not archive national sample', error.message));
        }
        setKeyForNationalSampleToBeArchived('');
    };

    const handleRestoreNationalSample = (profileId: string) => {
        const profileToRestore = nationalProfiles?.find(p => p.id === profileId);
        if (profileToRestore) {
            clientManagementApiClient.putPartnerDashboardProfile({
                ...profileToRestore,
                activeClient: true,
                archived: false,
            }).then(() => {
                setRestoredNationalSampleName(profileToRestore.displayName);
                setNationalFilterOption("activeClients");
                onRefreshProfiles?.();
            }).catch(error => console.error('Could not restore national sample', error.message));
        }
    };

    if (isFetching) {
        return <LoadingIndicator />
    }

    return <Grid fluid>
        <Row>
            <Col>
                <div className="client-table">
                    {shouldShowNationalSamples && (
                        <>
                            {restoredNationalSampleName !== '' && (
                                <div className="profile-restore-banner">
                                    <AlertBanner
                                        className={'marginbottom-lg'}
                                        fullWidth={false}
                                        icon="check_circle"
                                        type="success"
                                        showAlert={restoredNationalSampleName !== ''}
                                        showCloseBtn={true}
                                        message={`${restoredNationalSampleName} profile restored`}
                                        onClose={() => setRestoredNationalSampleName('')}
                                    />
                                </div>
                            )}
                            <Row>
                                <DashboardSearch
                                    id="national_search_container"
                                    name="national_search_standalone"
                                    value={nationalSearchText}
                                    onSearch={handleNationalSearch}
                                />
                                {shouldShowNationalSamplesFilter && (
                                    <Dropdown
                                        className="clientsFilterField marginleft-lg"
                                        data-testid="national-samples-filter"
                                        name="nationalSamplesFilter"
                                        id="nationalSamplesFilterInput"
                                        aria-label="nationalSamplesFilter"
                                        aria-labelledby="nationalSamplesFilterLabel"
                                        alignRight={false}
                                        buttonIcon="only"
                                        buttonKind="borderless"
                                        defaultText="Select..."
                                        label="SHOW"
                                        inlineLabel
                                        defaultPageSize={15}
                                        dropdownKind="select"
                                        dynamicDropAlign={false}
                                        dynamicDropDirection={false}
                                        iconNameClose="arrow_drop_up"
                                        iconNameOpen="arrow_drop_down"
                                        labelWidth="300px"
                                        nativeOnMobile={false}
                                        value={nationalFilterOption}
                                        panelHeight="auto"
                                        panelWidth="300px"
                                        buttonSize="medium"
                                        virtualScroll={false}
                                        onChange={(data: any) => setNationalFilterOption(data?.value as NationalSampleFilterOption)}
                                        disabled={isLoading}
                                    >
                                        <DropdownItem
                                            key="activeNational"
                                            itemText="Active National Samples"
                                            value="activeClients" />
                                        <DropdownItem
                                            key="archivedNational"
                                            itemText="Archived National Samples"
                                            value="archivedClients" />
                                    </Dropdown>
                                )}
                                {renderCreateNewProfileButton("NavigateToNewClientProfileFromNational")}
                            </Row>
                            <NationalSamplesSection
                                profiles={nationalSampleProfiles ?? []}
                                isLoading={isLoading}
                                isDuplicatingNationalSample={isDuplicatingNationalSample}
                                filterOption={nationalFilterOption}
                                isNationalSampleAdmin={isNationalSampleAdmin}
                                onDuplicateNationalSample={handleDuplicateNationalSample}
                                onArchiveNationalSample={handleArchiveNationalSample}
                                onRestoreNationalSample={handleRestoreNationalSample}
                                searchQuery={nationalSearchText}
                                isSearchActive={!!nationalSearchText}
                            />
                        </>
                    )}
                    <Row>
                        <DashboardSearch
                            id="search_container"
                            name="search_standalone"
                            value={searchText}
                            onSearch={handleSearch}
                        />
                        <Dropdown
                            className="clientsFilterField marginleft-lg"
                            data-testid='clients-filter'
                            name="clientsFilter"
                            id="clientsFilterInput"
                            aria-label="clientsFilter"
                            aria-labelledby="clientsFilterLabel"
                            alignRight={false}
                            buttonIcon="only"
                            buttonKind="borderless"
                            defaultText="Select..."
                            label="SHOW"
                            inlineLabel
                            key={dropdownKey}
                            defaultPageSize={15}
                            dropdownKind="select"
                            dynamicDropAlign={false}
                            dynamicDropDirection={false}
                            iconNameClose="arrow_drop_up"
                            iconNameOpen="arrow_drop_down"
                            labelWidth="300px"
                            nativeOnMobile={false}
                            value="activeClients"
                            panelHeight="auto"
                            panelWidth="300px"
                            buttonSize="medium"
                            virtualScroll={false}
                            onChange={(data: any) => handleDropdown(data?.value as FilterProfileOptions)}
                            disabled={isLoading}
                        >
                            <DropdownItem
                                key="activeClients"
                                itemText={`${tabFormattedText(tabType, enableCustomProfile).activeProfilesDropdown}`}
                                value="activeClients"
                            />
                            <DropdownItem
                                key="archivedClients"
                                itemText={`${tabFormattedText(tabType, enableCustomProfile).archivedClientsDropdown}`}
                                value="archivedClients"
                            />
                            {(tabType !== ClientType.PROSPECT && tabType !== ClientType.SAMPLE) &&
                                <DropdownItem
                                    key="clientsWithConditions"
                                    itemText="Clients with Conditions"
                                    value="clientsWithConditions"
                                />}
                            {(tabType !== ClientType.PROSPECT && tabType !== ClientType.SAMPLE) &&
                                <DropdownItem
                                    key="legalAgreementsWithoutProfile"
                                    itemText="Legal Agreements without Profiles"
                                    value="legalAgreementsWithoutProfile"
                                />}
                        </Dropdown>
                        {hasProfilesWithConditions ?
                            <DropdownMultiselect
                                className="conditionsFilterField marginleft-lg"
                                name="conditionsFilter"
                                id="conditionsFilter"
                                aria-label="conditionsFilter"
                                aria-labelledby="conditionsFilterLabel"
                                alignRight={false}
                                buttonIcon="right"
                                buttonKind="secondary"
                                buttonSize="large"
                                defaultPageSize={5}
                                defaultText="FILTER"
                                disabled={false}
                                dropUp={false}
                                dropdownKind="menu"
                                dynamicDropAlign={false}
                                dynamicDropDirection={false}
                                iconNameClose="filter_list"
                                iconNameOpen="filter_list"
                                nativeOnMobile={false}
                                onChange={(item: { [key: number]: string }) => {
                                    const conditions = new Set(Object.values(item));
                                    setConditionFilters(conditions)
                                    setPartnerDashboardProfiles(filterOnProfileOptionsDropdown({
                                        ...defaultFilterSettings,
                                        conditionFilters: conditions
                                    }));
                                }}
                                open={false}
                                panelHeight="auto"
                                panelWidth={280}
                                required={false}
                                searchable={false}
                                size="medium"
                                value={null}
                                virtualScroll={false}
                                options={conditionFilterOptions}
                                removeMarginTop
                                resetText="Reset"
                                selected={Array.from(conditionFilters.values())}
                                selectAllLabel="Select All"
                                selectedDisplayTextAfter="items selected"
                                selectedDisplayTextBefore=""
                                selectedIndex={null}
                                showFullSelected={false}
                                hideFooterButtons={true}
                            /> : <React.Fragment />
                        }
                        {!shouldShowNationalSamples && renderCreateNewProfileButton("NavigateToNewClientProfile")}
                    </Row>
                    <div
                        className="profile-restore-banner"
                    >
                        {showProfileRestoreBanner && <AlertBanner
                            className={'marginbottom-lg'}
                            fullWidth={false}
                            icon="check_circle"
                            type="success"
                            showAlert={showProfileRestoreBanner}
                            showCloseBtn={true}
                            message={`${restoredProfileInfo.restoredProfileName}` + " profile restored"}
                            onClose={closeProfileRestoreBanner}
                        />
                        }
                    </div>
                    <div
                        className="table-header"
                        aria-label="allClientsLabel"
                    >
                        <h4>
                            <i className="icons dds-icons icon--size-18 icon-padding">
                                account_group
                            </i>
                            {shouldShowNationalSamples ? 'My Samples' : tabFormattedText(tabType, enableCustomProfile).tableHeader}
                        </h4>
                    </div>
                    <div className={`table-spacing ${shouldAddSplitSamplesEmptySpacing ? 'split-samples-empty-table-spacing' : ''}`}>
                        {hasLegalAgreementsWithoutProfile && isLegalAgreementsLoading && <span data-testid='lawp-loading-indicator'><LoadingIndicator /></span>}

                        {hasLegalAgreementsWithoutProfile && !isLegalAgreementsLoading &&

                            <Table
                                id="LegalAgreementsWithoutProfile"
                                className="partner-dashboard-table"
                                caption="Legal Agreements Without Profile"
                                columns={legalAgreementsPageColumns}
                                data={filteredLegalAgreements}
                                minRows={PARTNER_DASHBOARD_MIN_ROWS}
                                filterable={false}
                                noDataText={loadProfileStatus()}
                                pageSizeDropUp={true}
                                pageSizeOptions={PARTNER_DASHBOARD_PAGE_SIZES}
                                rowHeight="medium"
                                showPagination={partnerDashboardProfiles.length > 0}
                                sortable={false}
                                defaultPageSize={dashboardVariables.PARTNER_DASHBOARD_DEFAULT_ROW}
                                SubComponent={null}
                            >
                            </Table>
                        }
                        {!hasLegalAgreementsWithoutProfile && <Table
                            id="AllclientsTable"
                            className="partner-dashboard-table"
                            caption="All Clients"
                            columns={clientsOrProspectsPageColumns}
                            data={partnerDashboardProfiles}
                            minRows={PARTNER_DASHBOARD_MIN_ROWS}
                            filterable={false}
                            noDataText={loadProfileStatus()}
                            pageSizeDropUp={true}
                            pageSizeOptions={PARTNER_DASHBOARD_PAGE_SIZES}
                            rowHeight="medium"
                            showPagination={partnerDashboardProfiles.length > 0}
                            sortable={false}
                            defaultPageSize={dashboardVariables.PARTNER_DASHBOARD_DEFAULT_ROW}
                            SubComponent={getSubComponent()}>
                        </Table>
                        }

                        <CustomModal isOpen={openArchiveClientModal}
                            title={`Archive this ${tabFormattedText(tabType, enableCustomProfile).routeName.toLowerCase()}?`}
                            content={`Are you sure you want to archive the selected ${tabFormattedText(tabType, enableCustomProfile).routeName.toLowerCase()}?`}
                            onClickCancel={() => setOpenArchiveClientModal(false)}
                            onClickConfirm={() => handleArchiveClient()}
                            cancelText='CANCEL'
                            confirmText={`ARCHIVE ${tabFormattedText(tabType, enableCustomProfile).routeName.toUpperCase()}`}
                        />
                        <CustomModal isOpen={openArchiveNationalSampleModal}
                            title="Archive this national sample?"
                            content="Are you sure you want to archive the selected national sample?"
                            onClickCancel={() => setOpenArchiveNationalSampleModal(false)}
                            onClickConfirm={confirmArchiveNationalSample}
                            cancelText='CANCEL'
                            confirmText='ARCHIVE NATIONAL SAMPLE'
                        />
                        <CustomModal isOpen={openConvertToClientModal}
                            title={'Convert to Client Profile?'}
                            content={'Are you sure you want to convert this Prospect Profile to a Client Profile? This profile will be moved to the Active Client List on the Partner Dashboard. ' +
                                'To provide access to the Service Team, a PW Client Profile must be linked via Profile Settings.'}
                            onClickCancel={() => setOpenConvertToClientModal(false)}
                            onClickConfirm={() => handleConvertToClient()}
                            cancelText='CANCEL'
                            confirmText={'CONVERT TO CLIENT'}
                        />
                        <ConfirmationModalForIPRestore isOpen={openRestoreClientInfoModal}
                            title={`Restored profile’s Investment Program is linked to another profile`}
                            content={
                                <div>
                                    <p style={{ margin: "0", padding: "0" }}>The following
                                        Investment Program is linked to another client
                                        profile and cannot be added to this restored
                                        profile.</p>
                                    <p />
                                    <p style={{ margin: "0", padding: "0" }}><span
                                        style={{ fontWeight: "bold" }}>Restored profile: {restoredProfileInfo.restoredProfileName} </span>
                                    </p>
                                    <p style={{ margin: "0", padding: "0" }}>Investment
                                        Program: {restoredProfileInfo.ipName} </p>
                                    <div style={{ marginLeft: "20px" }}>
                                        <ul style={{ marginTop: "0" }}>
                                            <li>Linked to
                                                profile: {restoredProfileInfo.profileName} </li>
                                            <li>Profile
                                                owner: {restoredProfileInfo.profileOwner} </li>
                                        </ul>
                                    </div>
                                </div>
                            }
                            onClickConfirm={() => setOpenRestoreClientInfoModal(false)}
                            confirmText={'OK'}
                        />

                        <ConfirmationModalForIPRestore isOpen={openRestoreClientInfoModalForInvalidIPStatus}
                            title={`Restored profile's Investment Program is invalid`}
                            content={
                                <div>
                                    <p style={{ margin: "0", padding: "0" }}>Please validate
                                        the Investment Program in Groupings Management.</p>
                                    <p style={{ margin: "0", padding: "0" }}><a
                                        className="nav-drawer-side-menu__share-point-link"
                                        href={restoredProfileInfo.wdsGMUrl}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        View Groupings Management
                                    </a></p>
                                    <p />
                                    <p style={{ margin: "0", padding: "0" }}><span
                                        style={{ fontWeight: "bold" }}>Restored profile: {restoredProfileInfo.restoredProfileName} </span>
                                    </p>
                                    <p style={{ margin: "0", padding: "0" }}>Investment
                                        Program: {restoredProfileInfo.ipName} </p>
                                </div>
                            }
                            onClickConfirm={() => setOpenRestoreClientInfoModalForInvalidIPStatus(false)}
                            confirmText={'OK'}
                        />
                        {enableCustomProfile && (
                            <CreateProfileModal
                                isOpen={openCustomModal}
                                title="Create Profile"
                                content="Select a profile type to create"
                                showCloseButton={true}
                                onRequestClose={() => setOpenCustomModal(false)}
                                onClickClientConfirm={() => {
                                    setOpenCustomModal(false);
                                    handleNewCustomClientNavigation('Client');
                                }}
                                onClickProspectConfirm={() => {
                                    setOpenCustomModal(false);
                                    handleNewCustomClientNavigation('Prospect');
                                }}
                                onClickSampleConfirm={() => {
                                    setOpenCustomModal(false);
                                    handleNewCustomClientNavigation('Sample');
                                }}
                            />
                        )}

                        {isLoading ? (
                            <div className="loading-dashboard">
                                <Loader message="Loading" size="md" />
                            </div>
                        ) : null}
                    </div>
                </div>
            </Col>
        </Row>
    </Grid>;

};

const tabFormattedText = (tabType: ClientType, enableCustomProfileType: boolean) => {
    switch (tabType) {
        case ClientType.CLIENT:
            return {
                createNewButton: enableCustomProfileType ? 'CREATE PROFILE' : 'CREATE NEW CLIENT',
                tableHeader: 'All Clients',
                activeProfilesDropdown: 'Active Clients',
                archivedClientsDropdown: 'Archived Clients',
                noMatchingText: 'clients',
                noArchivedMatchingText: 'profiles',
                routeName: 'Client'
            }
        case ClientType.PROSPECT:
            return {
                createNewButton: enableCustomProfileType ? 'CREATE PROFILE' : 'CREATE NEW PROSPECT',
                tableHeader: 'All Prospects',
                activeProfilesDropdown: 'Active Prospects',
                archivedClientsDropdown: 'Archived Prospects',
                noMatchingText: 'prospects',
                noArchivedMatchingText: 'prospects',
                routeName: 'Prospect'
            }
        case ClientType.SAMPLE:
            return {
                createNewButton: enableCustomProfileType ? 'CREATE PROFILE' : 'CREATE NEW SAMPLE',
                tableHeader: 'All Samples',
                activeProfilesDropdown: 'Active Samples',
                archivedClientsDropdown: 'Archived Samples',
                noMatchingText: 'samples',
                noArchivedMatchingText: 'samples',
                routeName: 'Sample'
            }
    }
}


const getSubComponentTable = (profileForSubComponent: PartnerDashboardProfile, conditionResultResponses: ConditionResultResponse[]) => {
    return (
        <Table
            className="dashboard-conditions-table"
            filterable={false}
            rowHeight="medium"
            showPagination={false}
            sortable={false}
            columns={[
                {
                    headerAttrs: { hidden: true },
                    Cell: () => {
                        return <div className={'dashboard-conditions-row-divider'}>&nbsp;</div>
                    },
                    width: 60,
                    maxWidth: 60
                },
                {
                    headerAttrs: { hidden: true },
                    Cell: ({
                        original,
                    }: {
                        original: Condition;
                    }) => {
                        return <span className="condition-detail">
                            {switchCondition(original.conditionName, original.profileData, original.asOfDate, original.activeDays, original.referenceId, original.profileData, original.conditionValue)}
                        </span>
                    },
                },
                {
                    headerAttrs: { hidden: true },
                    Cell: () => {
                        return <div className={'dashboard-conditions-row-divider'}>&nbsp;</div>
                    },
                    minWidth: 130,
                    maxWidth: 130
                },
                {
                    headerAttrs: { hidden: true },
                    Cell: () => {
                        return <div className={'dashboard-conditions-row-divider'}>&nbsp;</div>
                    },
                    minWidth: 120,
                    maxWidth: 120
                },
                {
                    headerAttrs: { hidden: true },
                    Cell: ({
                        original,
                    }: {
                        original: Condition;
                    }) => {
                        return <span>{original.activeDays} days</span>
                    },
                    minWidth: 110,
                    maxWidth: 110
                },
                {
                    headerAttrs: { hidden: true },
                    Cell: () => {
                        return <div className={'dashboard-conditions-row-divider'}>&nbsp;</div>
                    },
                    width: 110,
                    maxWidth: 110
                },
                {
                    headerAttrs: { hidden: true },
                    Cell: () => {
                        return <div className={'dashboard-conditions-row-divider'}>&nbsp;</div>
                    },
                    width: 110,
                    maxWidth: 110
                },
                {
                    headerAttrs: { hidden: true },
                    Cell: () => {
                        return <div className={'dashboard-conditions-row-divider'}>&nbsp;</div>
                    },
                    width: 150,
                    maxWidth: 150
                },
                {
                    headerAttrs: { hidden: true },
                    Cell: () => {
                        return <div className={'overflow-visible'}>&nbsp;</div>
                    },
                    minWidth: 32,
                    maxWidth: 32
                },

            ]}
            data={conditionResultResponses.filter((c) => c.profileId === profileForSubComponent.id)[0]?.conditions}
        >
        </Table>
    );
}

function switchCondition(conditonName: string, assetName?: string, asOfDate?: string, _activeDays?: number, referenceId?: string, profileData?: string, conditionValue?: string) {
    switch (conditonName) {
        case 'Risk Control Assets Underfunded':
            return (
                <span>Risk Control Assets Underfunded by {conditionValue}% ({'>'}5%). The current allocation is {profileData}% and the proposed is {referenceId}%.</span>);
        case 'Standalone Update Overdue':
            return (
                <span>Standalone Update Overdue. {assetName} was last updated {moment().diff(moment(asOfDate), 'days')} days ago ({'>'}365 days).</span>);
        case 'Standalone Update Non-Investable Overdue':
            return (
                <span>Standalone Update Overdue. {assetName} was last updated {moment().diff(moment(asOfDate), 'days')} days ago ({'>'}730 days).</span>);
        case 'Risk Assets Underfunded':
            return (
                <span>Risk Assets Underfunded by {conditionValue}% ({'>'}10%). The current allocation is {profileData}% and the proposed is {referenceId}%.</span>);
        case 'Investable Column Insufficient':
            return (
                <span>There is an Investable Column Insufficient of {conditionValue ? formatCurrency(parseFloat(conditionValue)) : '$0'} ({'<'}0) </span>);
        case 'All Column Insufficient':
            return (
                <span>The All Column is Insufficient by {conditionValue ? formatCurrency(parseFloat(conditionValue)) : '$0'} ({'<'}0)</span>);
        case 'GDWM Annual Report':
            return (
                <span>GDWM Annual Report {asOfDate ? 'last sent' : 'not sent'} {asOfDate ? asOfDate.substring(5, 7) + "/" + asOfDate.substring(8, 10) + "/" + asOfDate.substring(0, 4) : ''} ({'>'}365 days)</span>);
        case 'Equity Comp Update Overdue':
            return (
                <span>Equity Comp Update Overdue. {assetName} was last updated {moment().diff(moment(asOfDate), 'days')} days ago ({'>'}365 days).</span>);
        default:
            return conditonName;
    }
}

export default PartnerDashboard;
